// third-party
import * as yup from 'yup';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { Button, CardContent, DialogTitle, Divider, InputLabel, Stack, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// project import
import RequiredAsterisk from 'components/RequiredAsterisk';
import { dispatch, useSelector } from 'store';
import { closeJobTitleForm } from 'store/reducers/job-title';
import JobTitleDto from 'common/dto-types/contacts/job-title-dto';
import { createJobTitle, updateJobTitle } from './api';
import { openSnackbar } from 'store/reducers/snackbar';

type JobTitleFromProps = {
    refreshCallback: () => void;
};

const JobTitleForm = ({ refreshCallback }: JobTitleFromProps): JSX.Element => {
    const data = useSelector((state) => state.jobTitle.data);
    const isCreating = !data;

    const initialValues: JobTitleDto = {
        id: data?.id ?? 0,
        name: data?.name ?? '',
    };

    const validationSchema = yup.object({
        name: yup.string().required('Name is required').max(255, 'Name cannot exceed 255 characters'),
    });

    const handleFormSubmit = async (values: JobTitleDto, { setSubmitting }: FormikHelpers<JobTitleDto>) => {
        setSubmitting(true);
        let data;
        let confirmMessage;
        let failedMessage;

        if (isCreating) {
            data = await createJobTitle(values);
            confirmMessage = 'Job Title created successfully!';
            failedMessage = 'Failed to create Job Title';
        } else {
            data = await updateJobTitle(values);
            confirmMessage = 'Job Title updated successfully!';
            failedMessage = 'Failed to update Job Title';
        }

        if (data) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: confirmMessage,
                    variant: 'alert',
                    alert: {
                        color: 'success',
                    },
                }),
            );
            dispatch(closeJobTitleForm());
            refreshCallback();
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    message: failedMessage,
                    variant: 'alert',
                    alert: {
                        color: 'error',
                    },
                }),
            );
            setSubmitting(false);
        }
    };

    const formik = useFormik<JobTitleDto>({
        initialValues,
        validationSchema,
        onSubmit: handleFormSubmit,
    });

    const { errors, touched, dirty, isValid, getFieldProps, handleSubmit, isSubmitting } = formik;

    return (
        <FormikProvider value={formik}>
            <Form onSubmit={handleSubmit}>
                <DialogTitle>{isCreating ? 'Add' : 'Update'} JobTitle</DialogTitle>
                <CardContent>
                    <Stack spacing={3}>
                        <Stack spacing={1}>
                            <InputLabel>
                                JobTitle Name <RequiredAsterisk />
                            </InputLabel>
                            <TextField
                                fullWidth
                                id="name"
                                placeholder="Enter Job Title Name"
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                {...getFieldProps('name')}
                            />
                        </Stack>
                    </Stack>
                </CardContent>
                <Divider />
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2.5, py: 2 }}>
                    <Button color="error" size="small" onClick={() => dispatch(closeJobTitleForm())}>
                        Cancel
                    </Button>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        size="small"
                        loading={isSubmitting}
                        disabled={!isValid || (isCreating && !dirty)}
                    >
                        Save
                    </LoadingButton>
                </Stack>
            </Form>
        </FormikProvider>
    );
};

export default JobTitleForm;
